import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getRestaurants, getFacets } from '../services/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MapPin, Star, ChefHat, ChevronRight, Utensils } from 'lucide-react';
import HygieneBadge from '../components/HygieneBadge';

const CATEGORY_OPTIONS = [
  { value: 'restaurant', label: 'Restaurants' },
  { value: 'cafe', label: 'Cafés' },
  { value: 'takeaway', label: 'Takeaways' },
  { value: 'pub', label: 'Pubs & Bars' },
  { value: 'all', label: 'All venues' },
];

const RestaurantsPage = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('restaurant');
  const [cuisineGroup, setCuisineGroup] = useState('all');
  const [cuisineGroups, setCuisineGroups] = useState([]);
  const [visibleCount, setVisibleCount] = useState(24);

  useEffect(() => {
    getFacets().then((r) => setCuisineGroups(r.data.cuisine_groups || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await getRestaurants(null, category, cuisineGroup);
        setRestaurants(res.data);
        setVisibleCount(24);
      } catch (error) {
        console.error('Error fetching restaurants:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [category, cuisineGroup]);

  const defaultImages = [
    'https://images.pexels.com/photos/6205791/pexels-photo-6205791.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    'https://images.pexels.com/photos/33254643/pexels-photo-33254643.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    'https://images.pexels.com/photos/36605410/pexels-photo-36605410.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="restaurants-page">
      {/* Header */}
      <section className="bg-[#7B2CBF] py-10">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white text-center mb-2">
            Restaurants
          </h1>
          <p className="text-white/80 text-center max-w-xl mx-auto">
            Discover local restaurants, cafés, takeaways and pubs
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-[#E8E8E8] bg-[#F5F5F5]">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-6">
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-[180px] bg-white" data-testid="restaurants-category-select">
                <SelectValue placeholder="Restaurants" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={cuisineGroup} onValueChange={setCuisineGroup}>
              <SelectTrigger className="w-[180px] bg-white" data-testid="restaurants-cuisine-select">
                <SelectValue placeholder="All cuisines" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All cuisines</SelectItem>
                {cuisineGroups.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Restaurants Grid */}
      <section className="py-8 sm:py-12">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white border border-[#E8E8E8] rounded-xl overflow-hidden animate-pulse">
                  <div className="w-full h-40 bg-[#F5F5F5]" />
                  <div className="p-4">
                    <div className="h-5 bg-[#F5F5F5] rounded w-3/4 mb-2" />
                    <div className="h-4 bg-[#F5F5F5] rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : restaurants.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {restaurants.slice(0, visibleCount).map((restaurant, index) => (
                <Link 
                  key={restaurant.id}
                  to={`/restaurant/${restaurant.id}`}
                  className="deal-card bg-white rounded-xl overflow-hidden flex flex-col h-full opacity-0 animate-fade-in-up"
                  style={{ animationDelay: `${(index % 24) * 0.05}s`, animationFillMode: 'forwards' }}
                  data-testid={`restaurant-card-${restaurant.id}`}
                >
                  {/* Image */}
                  <div className="relative w-full h-40 bg-[#F5F5F5]">
                    <img 
                      src={restaurant.image_url || defaultImages[index % defaultImages.length]} 
                      alt={restaurant.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = defaultImages[0]; }}
                    />
                    {restaurant.avg_rating > 0 && (
                      <div className="absolute top-3 right-3 bg-white text-[#242E30] flex items-center gap-1 px-2 py-1 rounded-md text-sm font-semibold shadow-sm">
                        <Star className="h-3.5 w-3.5 fill-[#FFB800] text-[#FFB800]" />
                        {restaurant.avg_rating.toFixed(1)}
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4 flex-1 flex flex-col">
                    <h3 className="font-bold text-[#242E30] text-base mb-1">{restaurant.name}</h3>
                    <p className="text-sm text-[#595959] mb-1 flex items-center gap-1.5">
                      <Utensils className="h-3.5 w-3.5 text-[#7B2CBF] shrink-0" />
                      {restaurant.cuisine || 'Restaurant'}
                    </p>
                    {(restaurant.town_name || restaurant.postcode_outward) && (
                      <p className="text-sm text-[#595959] mb-2 flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5 text-[#7B2CBF] shrink-0" />
                        {[restaurant.town_name, restaurant.postcode_outward].filter(Boolean).join(', ')}
                      </p>
                    )}
                    <div className="flex-1" />
                    {restaurant.hygiene_rating ? (
                      <div className="mb-3">
                        <HygieneBadge rating={restaurant.hygiene_rating} />
                      </div>
                    ) : null}
                    <span className="inline-flex items-center gap-1 text-[#7B2CBF] font-semibold text-sm">
                      Venue Details
                      <ChevronRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-[#F5F5F5] rounded-xl">
              <ChefHat className="h-16 w-16 text-[#D4D4D4] mx-auto mb-4" />
              <h3 className="text-lg font-bold text-[#242E30] mb-2">No Restaurants Yet</h3>
              <p className="text-[#595959] max-w-md mx-auto mb-6">
                Be the first to list your restaurant!
              </p>
              <Link
                to="/register?role=restaurant"
                className="inline-flex items-center gap-2 bg-[#7B2CBF] text-white hover:bg-[#5A189A] px-6 py-3 font-semibold rounded-lg transition-colors"
              >
                List your restaurant
                <ChevronRight className="h-5 w-5" />
              </Link>
            </div>
          )}

          {!loading && restaurants.length > visibleCount && (
            <div className="flex justify-center mt-8">
              <button
                onClick={() => setVisibleCount((c) => c + 24)}
                className="bg-white border border-[#7B2CBF] text-[#7B2CBF] hover:bg-[#7B2CBF] hover:text-white px-8 py-3 rounded-lg font-semibold transition-colors"
                data-testid="restaurants-load-more-btn"
              >
                Load more ({restaurants.length - visibleCount} remaining)
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default RestaurantsPage;
